/**
 * TelemetryContext — keeps correlation headers (`x-pcb-*`) in sync with the
 * active user + session.
 *
 * Today the headers are passed to the ApiClient as a simple object that the
 * http layer reads off the options bag. When the api-client gains a request
 * interceptor, wire this through to the shared axios/fetch layer.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';

export interface TelemetryHeaders {
  'x-pcb-app': string;
  'x-pcb-app-version': string;
  'x-pcb-user': string;
  'x-pcb-session-id': string;
  'x-pcb-correlation-id'?: string;
}

export interface TelemetryContextValue {
  headers: TelemetryHeaders;
  sessionId: string;
  setCorrelationId: (id: string) => void;
}

const TelemetryContext = createContext<TelemetryContextValue | null>(null);

function genSessionId(): string {
  // RFC-4122-lite; crypto.randomUUID exists in modern runtimes
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `sid-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

export function TelemetryProvider({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const sessionIdRef = useRef<string>(genSessionId());
  const correlationIdRef = useRef<string | null>(null);

  const headers = useMemo<TelemetryHeaders>(() => {
    const base: TelemetryHeaders = {
      'x-pcb-app': 'ROP-teams',
      'x-pcb-app-version':
        (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.1.0',
      'x-pcb-user': user?.upn ?? 'anonymous',
      'x-pcb-session-id': sessionIdRef.current,
    };
    if (correlationIdRef.current) {
      base['x-pcb-correlation-id'] = correlationIdRef.current;
    }
    return base;
  }, [user]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.info('[telemetry] session started', headers);
  }, [headers]);

  const value = useMemo<TelemetryContextValue>(
    () => ({
      headers,
      sessionId: sessionIdRef.current,
      setCorrelationId: (id: string) => { correlationIdRef.current = id; },
    }),
    [headers],
  );

  return <TelemetryContext.Provider value={value}>{children}</TelemetryContext.Provider>;
}

export function useTelemetry(): TelemetryContextValue {
  const ctx = useContext(TelemetryContext);
  if (!ctx) throw new Error('useTelemetry must be used within TelemetryProvider');
  return ctx;
}
